/**
 * Upload plus extraction as one step, because that is how it feels to the person: drop a receipt,
 * get a filled-in document back.
 *
 * Runs one file at a time and keeps per-file state, so a failed OCR on page three does not lose
 * the two that already worked. A file that uploads but cannot be read still exists as a document
 * in the inbox - never silently dropped.
 *
 * A photo is scanned in the browser first, like in the phone app (lib/scan/photo.ts): the upload
 * is the black and white scan plus a compressed copy of the photo, not the camera's file.
 */
import { useCallback, useState } from "react";
import { extractDocument, extractionToPatch, scanToPatch } from "@/lib/api/extract";
import { sendCapture } from "@/lib/api/capture";
import {
  isImageFile,
  updateDocument,
  uploadDocument,
  type DocumentKind,
} from "@/lib/api/documents";
import { scanPdf, scanPhoto } from "@/lib/scan/photo";
import { useQueryClient } from "@tanstack/react-query";
import { documentKeys } from "@/hooks/use-documents";
import { track } from "@/lib/platform/analytics";

export type UploadState = "uploading" | "extracting" | "done" | "error";

export type UploadItem = {
  name: string;
  state: UploadState;
  documentId?: string;
  confidence?: number;
  error?: string;
};

export type UploadContext = {
  workspaceId: string;
  userId: string;
  ownVatId?: string;
  ownIban?: string;
  ownNames?: string;
};

export function useDocumentUpload(context: UploadContext) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  const patch = useCallback((index: number, changes: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item, position) => (position === index ? { ...item, ...changes } : item)),
    );
  }, []);

  const upload = useCallback(
    async (files: File[], kind: DocumentKind) => {
      if (files.length === 0) return [];
      setBusy(true);
      setItems(files.map((file) => ({ name: file.name, state: "uploading" as UploadState })));
      const created: string[] = [];

      for (const [index, file] of files.entries()) {
        try {
          // A photo the browser cannot decode (a HEIC in Chrome) goes up as it is, scanned there.
          const scanned = isImageFile(file)
            ? await scanPhoto(file, file.name).catch(() => null)
            : null;
          if (scanned) {
            const outcome = await sendCapture(
              {
                id: file.name,
                workspaceId: context.workspaceId,
                kind,
                pages: [scanned.original],
                scan: { pdf: await scanPdf([scanned], file.name), pages: [scanned.page] },
                createdAt: new Date().toISOString(),
                attempts: 0,
              },
              {
                userId: context.userId,
                ownVatId: context.ownVatId,
                ownIban: context.ownIban,
                ownNames: context.ownNames,
              },
              async (documentId) => {
                created.push(documentId);
                patch(index, { state: "extracting", documentId });
              },
            );
            patch(index, { state: "done", confidence: outcome.confidence, error: outcome.error });
            if (outcome.extracted)
              track("document_extracted", { kind, confidence: outcome.confidence ?? 0 });
            track("document_uploaded", { kind });
            continue;
          }

          const document = await uploadDocument({
            workspace: context.workspaceId,
            kind,
            file,
            uploadedBy: context.userId,
          });
          created.push(document.id);
          patch(index, { state: "extracting", documentId: document.id });

          try {
            const result = await extractDocument(file, {
              kind,
              ownVatId: context.ownVatId,
              ownIban: context.ownIban,
              ownNames: context.ownNames,
            });
            // A photo comes back straightened: the scan becomes the file, the photo stays as original.
            await updateDocument(document.id, {
              ...extractionToPatch(result),
              ...scanToPatch(result, file.name),
            });
            patch(index, { state: "done", confidence: result.confidence });
            track("document_extracted", { kind, confidence: result.confidence });
          } catch (error) {
            // The document is saved; only reading it failed. Mark it so the inbox shows why.
            await updateDocument(document.id, { ocrStatus: "failed", status: "review" });
            patch(index, {
              state: "done",
              error: error instanceof Error ? error.message : String(error),
            });
          }
          track("document_uploaded", { kind });
        } catch (error) {
          patch(index, {
            state: "error",
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      setBusy(false);
      return created;
    },
    [
      context.workspaceId,
      context.userId,
      context.ownVatId,
      context.ownIban,
      context.ownNames,
      patch,
      queryClient,
    ],
  );

  const reset = useCallback(() => setItems([]), []);

  return { upload, items, busy, reset };
}
